import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import { ChevronUpIcon } from '../../components/atoms/icons/ChevronUpIcon';
import theme from '../../theme';
import { usePrototype } from './PrototypeContext';

interface FAQ {
  id: string;
  question: string;
  cohorts: string[];
  audience: number;
  storyblokId?: number;
}

interface FAQCategory {
  id: string;
  name: string;
  count: number;
  faqs: FAQ[];
}

const STORYBLOK_SPACE_ID = '572723165993589';
const getStoryblokEditUrl = (storyId: number) =>
  `https://app.storyblok.com/#/me/spaces/${STORYBLOK_SPACE_ID}/stories/0/0/${storyId}`;

const initialCategories: FAQCategory[] = [
  {
    id: '1',
    name: 'About the rewards program',
    count: 3,
    faqs: [
      {
        id: '1',
        question: 'How do I earn rewards?',
        cohorts: ['All members'],
        audience: 5,
        storyblokId: 162992558818154,
      },
      {
        id: '2',
        question: 'How much can I earn?',
        cohorts: ['All members'],
        audience: 4,
        storyblokId: 162992558818155,
      },
      {
        id: '3',
        question: 'What is the Kaiser Permanente rewards program?',
        cohorts: ['All members'],
        audience: 5,
        storyblokId: 162992558814057,
      },
    ],
  },
  {
    id: '2',
    name: 'Getting started with rewards',
    count: 2,
    faqs: [
      {
        id: '4',
        question: 'Where can I edit my communication preferences?',
        cohorts: ['All members'],
        audience: 3,
        storyblokId: 162992558969709,
      },
      {
        id: '5',
        question: 'How do I update my rewards email address?',
        cohorts: ['All members'],
        audience: 3,
        storyblokId: 162992558969710,
      },
    ],
  },
  {
    id: '3',
    name: 'Earning rewards',
    count: 2,
    faqs: [
      {
        id: '6',
        question: 'What is a health adventure and how does it work?',
        cohorts: ['All members'],
        audience: 4,
        storyblokId: 162992559104880,
      },
      {
        id: '7',
        question: 'Can I change my health adventure?',
        cohorts: ['All members'],
        audience: 3,
        storyblokId: 162992559104881,
      },
    ],
  },
];

// Breadcrumb Component
interface BreadcrumbProps {
  items: string[];
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ items }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
    {items.map((item, index) => (
      <React.Fragment key={item}>
        <Typography
          variant={index === items.length - 1 ? 'breadcrumbActive' : 'breadcrumbInactive'}
          sx={{ fontFamily: 'Manrope, sans-serif' }}
        >
          {item}
        </Typography>
        {index < items.length - 1 && <Typography variant="breadcrumbInactive">{' > '}</Typography>}
      </React.Fragment>
    ))}
  </Box>
);

// Category Accordion Component
interface CategoryAccordionProps {
  category: FAQCategory;
  isExpanded: boolean;
  onToggle: () => void;
  onNewQuestion?: () => void;
  onRename?: () => void;
  onDelete?: () => void;
  showRename?: boolean;
  showDelete?: boolean;
}

const CategoryAccordion: React.FC<CategoryAccordionProps> = ({
  category,
  isExpanded,
  onToggle,
  onNewQuestion,
  onRename,
  onDelete,
  showRename = false,
  showDelete = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Box
      sx={{
        bgcolor: '#FFFFFF',
        border: '1px solid #EFF0F0',
        borderRadius: 2,
        mb: 1,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          cursor: 'pointer',
          '&:hover': { bgcolor: '#FAFAFA' },
        }}
        onClick={onToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body1" fontWeight={500}>
            {category.name} ({category.count})
          </Typography>
          {/* Hover actions - Rename and Delete */}
          {isHovered && !isExpanded && (showRename || showDelete) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
              {showRename && (
                <Button
                  size="small"
                  startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRename?.();
                  }}
                  sx={{
                    color: theme.palette.textDarkPrimary.primary,
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: 13,
                    py: 0.25,
                    px: 1,
                    minWidth: 'auto',
                    bgcolor: '#FFFFFF',
                    border: '1px solid #E0E0E0',
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: '#F5F5F5',
                    },
                  }}
                >
                  Rename
                </Button>
              )}
              {showDelete && (
                <Button
                  size="small"
                  startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete?.();
                  }}
                  sx={{
                    color: theme.palette.textDarkPrimary.primary,
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: 13,
                    py: 0.25,
                    px: 1,
                    minWidth: 'auto',
                    bgcolor: '#FFFFFF',
                    border: '1px solid #E0E0E0',
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: '#F5F5F5',
                    },
                  }}
                >
                  Delete
                </Button>
              )}
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isExpanded && (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={(e) => {
                e.stopPropagation();
                onNewQuestion?.();
              }}
              sx={{
                color: theme.palette.textDarkPrimary.primary,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              New question
            </Button>
          )}
          <ChevronUpIcon
            sx={{
              width: 20,
              height: 20,
              transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 0.2s',
            }}
          />
        </Box>
      </Box>

      {/* Content */}
      <Collapse in={isExpanded}>
        {category.faqs.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#FAFAFA' }}>
                <TableCell padding="checkbox">
                  <Checkbox size="small" />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    Question description
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    Cohort
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    Audience
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    Actions
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {category.faqs.map((faq) => (
                <TableRow key={faq.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{faq.question}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {faq.cohorts.map((cohort, index) => (
                        <Chip
                          key={index}
                          label={cohort}
                          size="small"
                          sx={{
                            bgcolor:
                              cohort === 'Unassigned'
                                ? '#F7F5F2'
                                : theme.palette.accentsYellowA.light,
                            fontSize: 12,
                            height: 24,
                          }}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{faq.audience}</Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                      href={getStoryblokEditUrl(faq.storyblokId!)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: theme.palette.textDarkPrimary.primary,
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: 12,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Edit in CMS
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Collapse>
    </Box>
  );
};

// Create Category Dialog Component
interface CreateCategoryDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
  existingCategories: string[];
}

const CreateCategoryDialog: React.FC<CreateCategoryDialogProps> = ({
  open,
  onClose,
  onSave,
  existingCategories,
}) => {
  const [categoryName, setCategoryName] = useState('');
  const [touched, setTouched] = useState(false);

  const isDuplicate = existingCategories.some(
    (name) => name.toLowerCase() === categoryName.toLowerCase()
  );
  const isValid = categoryName.trim().length > 0 && !isDuplicate;
  const showError = touched && isDuplicate;

  const handleClose = () => {
    setCategoryName('');
    setTouched(false);
    onClose();
  };

  const handleSave = () => {
    if (isValid) {
      onSave(categoryName.trim());
      handleClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          fontFamily: 'Manrope, sans-serif',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600} sx={{ fontFamily: 'Manrope, sans-serif' }}>
            Create new category
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, fontFamily: 'Manrope, sans-serif' }}
          >
            This will create a new FAQ category visible in the portal.
            <br />
            You can add FAQs to it after creation.
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ mt: -0.5, mr: -1 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{ mb: 1, fontFamily: 'Manrope, sans-serif' }}
        >
          Category name <span style={{ color: '#EE7626' }}>*</span>
        </Typography>
        <TextField
          fullWidth
          placeholder="e.g., Prescription Benefits, Provider Network..."
          value={categoryName}
          onChange={(e) => {
            setCategoryName(e.target.value);
            setTouched(true);
          }}
          onBlur={() => setTouched(true)}
          error={showError}
          helperText={
            showError ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WarningAmberIcon sx={{ fontSize: 16, color: '#D32F2F' }} />
                Category name already exists. Please choose a different name.
              </Box>
            ) : null
          }
          sx={{
            '& .MuiOutlinedInput-root': {
              fontFamily: 'Manrope, sans-serif',
              '&.Mui-error': {
                '& fieldset': {
                  borderColor: '#D32F2F',
                },
              },
            },
            '& .MuiFormHelperText-root': {
              color: '#D32F2F',
              fontFamily: 'Manrope, sans-serif',
              ml: 0,
            },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderColor: '#E0E0E0',
            color: theme.palette.textDarkPrimary.primary,
            fontFamily: 'Manrope, sans-serif',
            px: 3,
            '&:hover': {
              borderColor: '#BDBDBD',
              bgcolor: '#FAFAFA',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isValid}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontFamily: 'Manrope, sans-serif',
            px: 3,
            bgcolor: isValid ? theme.palette.accentsYellowA.main : '#E0E0E0',
            color: isValid ? theme.palette.textDarkPrimary.primary : '#9E9E9E',
            '&:hover': {
              bgcolor: isValid ? theme.palette.accentsOrangeB.main : '#E0E0E0',
            },
            '&.Mui-disabled': {
              bgcolor: '#E0E0E0',
              color: '#9E9E9E',
            },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Rename Category Dialog Component
interface RenameCategoryDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (newName: string) => void;
  category: FAQCategory | null;
  existingCategories: string[];
}

const RenameCategoryDialog: React.FC<RenameCategoryDialogProps> = ({
  open,
  onClose,
  onSave,
  category,
  existingCategories,
}) => {
  const [categoryName, setCategoryName] = useState('');
  const [touched, setTouched] = useState(false);

  // Reset state when dialog opens with a new category
  React.useEffect(() => {
    if (open && category) {
      setCategoryName(category.name);
      setTouched(false);
    }
  }, [open, category]);

  const isDuplicate = existingCategories.some(
    (name) =>
      name.toLowerCase() === categoryName.toLowerCase() &&
      name.toLowerCase() !== category?.name.toLowerCase()
  );
  const isChanged = categoryName.trim() !== category?.name;
  const isValid = categoryName.trim().length > 0 && !isDuplicate && isChanged;
  const showError = touched && isDuplicate;

  const handleClose = () => {
    setCategoryName('');
    setTouched(false);
    onClose();
  };

  const handleSave = () => {
    if (isValid) {
      onSave(categoryName.trim());
      handleClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          fontFamily: 'Manrope, sans-serif',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600} sx={{ fontFamily: 'Manrope, sans-serif' }}>
            Rename category
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, fontFamily: 'Manrope, sans-serif' }}
          >
            Editing "{category?.name}"
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ mt: -0.5, mr: -1 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{ mb: 1, fontFamily: 'Manrope, sans-serif' }}
        >
          Category name <span style={{ color: '#EE7626' }}>*</span>
        </Typography>
        <TextField
          fullWidth
          value={categoryName}
          onChange={(e) => {
            setCategoryName(e.target.value);
            setTouched(true);
          }}
          onBlur={() => setTouched(true)}
          error={showError}
          helperText={
            showError ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WarningAmberIcon sx={{ fontSize: 16, color: '#D32F2F' }} />
                Category name already exists. Please choose a different name.
              </Box>
            ) : null
          }
          sx={{
            '& .MuiOutlinedInput-root': {
              fontFamily: 'Manrope, sans-serif',
              '&.Mui-error': {
                '& fieldset': {
                  borderColor: '#D32F2F',
                },
              },
            },
            '& .MuiFormHelperText-root': {
              color: '#D32F2F',
              fontFamily: 'Manrope, sans-serif',
              ml: 0,
            },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderColor: '#E0E0E0',
            color: theme.palette.textDarkPrimary.primary,
            fontFamily: 'Manrope, sans-serif',
            px: 3,
            '&:hover': {
              borderColor: '#BDBDBD',
              bgcolor: '#FAFAFA',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isValid}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontFamily: 'Manrope, sans-serif',
            px: 3,
            bgcolor: isValid ? theme.palette.accentsYellowA.main : '#E0E0E0',
            color: isValid ? theme.palette.textDarkPrimary.primary : '#9E9E9E',
            '&:hover': {
              bgcolor: isValid ? theme.palette.accentsOrangeB.main : '#E0E0E0',
            },
            '&.Mui-disabled': {
              bgcolor: '#E0E0E0',
              color: '#9E9E9E',
            },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Delete Category Dialog Component - Warning when category has questions
interface DeleteCategoryDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onMoveAndDelete: () => void;
  category: FAQCategory | null;
}

const DeleteCategoryDialog: React.FC<DeleteCategoryDialogProps> = ({
  open,
  onClose,
  onConfirm,
  onMoveAndDelete,
  category,
}) => {
  const hasQuestions = category && category.count > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          fontFamily: 'Manrope, sans-serif',
        },
      }}
    >
      {hasQuestions ? (
        // Warning dialog when category has questions
        <>
          <DialogContent sx={{ pt: 4, pb: 2, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: '#FEF3C7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 28, color: '#F59E0B' }} />
            </Box>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{ fontFamily: 'Manrope, sans-serif', mb: 1 }}
            >
              Delete "{category?.name}"?
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontFamily: 'Manrope, sans-serif' }}
            >
              This category has <strong>{category?.count} questions</strong>. You'll need to move
              them first.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 1 }}>
            <Button
              onClick={onClose}
              variant="outlined"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#E0E0E0',
                color: theme.palette.textDarkPrimary.primary,
                fontFamily: 'Manrope, sans-serif',
                px: 3,
                '&:hover': {
                  borderColor: '#BDBDBD',
                  bgcolor: '#FAFAFA',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={onMoveAndDelete}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontFamily: 'Manrope, sans-serif',
                px: 3,
                bgcolor: theme.palette.accentsYellowA.main,
                color: theme.palette.textDarkPrimary.primary,
                '&:hover': {
                  bgcolor: theme.palette.accentsOrangeB.main,
                },
              }}
            >
              Move questions & Delete
            </Button>
          </DialogActions>
        </>
      ) : (
        // Delete confirmation for empty category (count = 0)
        <>
          <DialogContent sx={{ pt: 4, pb: 2, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: '#FEF3C7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 28, color: '#F59E0B' }} />
            </Box>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{ fontFamily: 'Manrope, sans-serif', mb: 1 }}
            >
              Delete "{category?.name}"?
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontFamily: 'Manrope, sans-serif' }}
            >
              This empty category will be permanently deleted.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 1 }}>
            <Button
              onClick={onClose}
              variant="outlined"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#E0E0E0',
                color: theme.palette.textDarkPrimary.primary,
                fontFamily: 'Manrope, sans-serif',
                px: 3,
                '&:hover': {
                  borderColor: '#BDBDBD',
                  bgcolor: '#FAFAFA',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                onConfirm();
                onClose();
              }}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontFamily: 'Manrope, sans-serif',
                px: 3,
                bgcolor: theme.palette.accentsYellowA.main,
                color: theme.palette.textDarkPrimary.primary,
                '&:hover': {
                  bgcolor: theme.palette.accentsOrangeB.main,
                },
              }}
            >
              Yes, Delete
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

// Move Questions Dialog Component
interface MoveQuestionsDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (destinationId: string) => void;
  category: FAQCategory | null;
  categories: FAQCategory[];
}

const MoveQuestionsDialog: React.FC<MoveQuestionsDialogProps> = ({
  open,
  onClose,
  onConfirm,
  category,
  categories,
}) => {
  const [selectedDestination, setSelectedDestination] = useState('');

  // Sample questions to show in the preview
  const sampleQuestions = [
    'Questions about activating your benefit card',
    'Steps to enroll in a new plan',
    'Understanding your welcome packet',
  ];

  // Filter out the current category from destinations
  const availableDestinations = categories.filter((c) => c.id !== category?.id);

  const handleClose = () => {
    setSelectedDestination('');
    onClose();
  };

  const handleConfirm = () => {
    if (selectedDestination) {
      onConfirm(selectedDestination);
      handleClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          fontFamily: 'Manrope, sans-serif',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600} sx={{ fontFamily: 'Manrope, sans-serif' }}>
            Move {category?.count} questions
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, fontFamily: 'Manrope, sans-serif' }}
          >
            Choose a destination before deleting "{category?.name}".
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ mt: -0.5, mr: -1 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Questions preview box */}
        <Box
          sx={{
            bgcolor: '#F5F5F5',
            borderRadius: 2,
            p: 2,
            mb: 3,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: 'Manrope, sans-serif', display: 'block', mb: 1 }}
          >
            Questions to move:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {sampleQuestions.map((q, i) => (
              <Typography
                key={i}
                component="li"
                variant="body2"
                sx={{ fontFamily: 'Manrope, sans-serif', mb: 0.5 }}
              >
                {q}
              </Typography>
            ))}
          </Box>
        </Box>

        {/* Destination dropdown */}
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{ mb: 1, fontFamily: 'Manrope, sans-serif' }}
        >
          Move to <span style={{ color: '#EE7626' }}>*</span>
        </Typography>
        <Select
          fullWidth
          value={selectedDestination}
          onChange={(e) => setSelectedDestination(e.target.value)}
          displayEmpty
          sx={{
            fontFamily: 'Manrope, sans-serif',
            '& .MuiSelect-select': {
              fontFamily: 'Manrope, sans-serif',
            },
          }}
        >
          <MenuItem value="" disabled>
            <Typography color="text.secondary">Select</Typography>
          </MenuItem>
          {availableDestinations.map((cat) => (
            <MenuItem key={cat.id} value={cat.id}>
              {cat.name}
            </MenuItem>
          ))}
        </Select>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderColor: '#E0E0E0',
            color: theme.palette.textDarkPrimary.primary,
            fontFamily: 'Manrope, sans-serif',
            px: 3,
            '&:hover': {
              borderColor: '#BDBDBD',
              bgcolor: '#FAFAFA',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedDestination}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontFamily: 'Manrope, sans-serif',
            px: 3,
            bgcolor: selectedDestination ? theme.palette.accentsYellowA.main : '#E0E0E0',
            color: selectedDestination ? theme.palette.textDarkPrimary.primary : '#9E9E9E',
            '&:hover': {
              bgcolor: selectedDestination ? theme.palette.accentsOrangeB.main : '#E0E0E0',
            },
            '&.Mui-disabled': {
              bgcolor: '#E0E0E0',
              color: '#9E9E9E',
            },
          }}
        >
          Move & Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Master tenant categories with questions available for import
const masterCategoriesWithQuestions: FAQCategory[] = [
  {
    id: 'm1',
    name: 'Reward cards',
    count: 4,
    faqs: [
      {
        id: 'mq1',
        question: 'How do I check my reward card balance?',
        cohorts: ['All members'],
        audience: 5,
      },
      {
        id: 'mq2',
        question: 'Where can I use my reward card?',
        cohorts: ['18+ years'],
        audience: 3,
      },
      {
        id: 'mq3',
        question: 'What happens if my reward card is lost or stolen?',
        cohorts: ['All members'],
        audience: 2,
      },
      {
        id: 'mq4',
        question: 'How long does it take to receive my reward card?',
        cohorts: ['New members'],
        audience: 4,
      },
    ],
  },
  {
    id: 'm2',
    name: 'Enrollment and activation',
    count: 3,
    faqs: [
      {
        id: 'mq5',
        question: 'What documents do I need to activate my card?',
        cohorts: ['18+ years', '+2'],
        audience: 3,
      },
      {
        id: 'mq6',
        question: 'Can I enroll dependents after open enrollment?',
        cohorts: ['30 days', '1 week'],
        audience: 2,
      },
      {
        id: 'mq7',
        question: 'How long does activation take?',
        cohorts: ['All members'],
        audience: 4,
      },
    ],
  },
  {
    id: 'm3',
    name: 'Claims and billing',
    count: 3,
    faqs: [
      { id: 'mq8', question: 'How do I submit a claim?', cohorts: ['All members'], audience: 5 },
      {
        id: 'mq9',
        question: 'What is the claims processing time?',
        cohorts: ['All members'],
        audience: 3,
      },
      {
        id: 'mq10',
        question: 'How do I check my claim status?',
        cohorts: ['All members'],
        audience: 4,
      },
    ],
  },
  {
    id: 'm4',
    name: 'Coverage and benefits',
    count: 3,
    faqs: [
      {
        id: 'mq11',
        question: 'What services are covered under my plan?',
        cohorts: ['All members'],
        audience: 5,
      },
      {
        id: 'mq12',
        question: 'How do I find in-network providers?',
        cohorts: ['All members'],
        audience: 4,
      },
      {
        id: 'mq13',
        question: 'What is my deductible amount?',
        cohorts: ['All members'],
        audience: 3,
      },
    ],
  },
  {
    id: 'm5',
    name: 'Prescription benefits',
    count: 2,
    faqs: [
      {
        id: 'mq14',
        question: 'How do I refill my prescription?',
        cohorts: ['All members'],
        audience: 4,
      },
      {
        id: 'mq15',
        question: 'What pharmacies are in my network?',
        cohorts: ['All members'],
        audience: 3,
      },
    ],
  },
];

// Import Category Dialog Component
interface ImportCategoryDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (categories: FAQCategory[]) => void;
  existingCategories: FAQCategory[];
}

const ImportCategoryDialog: React.FC<ImportCategoryDialogProps> = ({
  open,
  onClose,
  onImport,
  existingCategories,
}) => {
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [selectedQuestions, setSelectedQuestions] = useState<Record<string, string[]>>({});

  // Get existing question IDs to filter them out
  const existingQuestionIds = existingCategories.flatMap((c) => c.faqs.map((f) => f.id));

  // Filter out questions that already exist
  const availableCategories = masterCategoriesWithQuestions
    .map((cat) => ({
      ...cat,
      faqs: cat.faqs.filter((faq) => !existingQuestionIds.includes(faq.id)),
    }))
    .filter((cat) => cat.faqs.length > 0);

  const handleToggleExpand = (categoryId: string) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]
    );
  };

  const handleToggleQuestion = (categoryId: string, questionId: string) => {
    setSelectedQuestions((prev) => {
      const categoryQuestions = prev[categoryId] || [];
      const newQuestions = categoryQuestions.includes(questionId)
        ? categoryQuestions.filter((id) => id !== questionId)
        : [...categoryQuestions, questionId];
      return { ...prev, [categoryId]: newQuestions };
    });
  };

  const handleToggleCategory = (categoryId: string) => {
    const category = availableCategories.find((c) => c.id === categoryId);
    if (!category) return;

    const categoryQuestions = selectedQuestions[categoryId] || [];
    const allSelected = categoryQuestions.length === category.faqs.length;

    setSelectedQuestions((prev) => ({
      ...prev,
      [categoryId]: allSelected ? [] : category.faqs.map((f) => f.id),
    }));
  };

  const isCategoryFullySelected = (categoryId: string) => {
    const category = availableCategories.find((c) => c.id === categoryId);
    if (!category) return false;
    const selected = selectedQuestions[categoryId] || [];
    return selected.length === category.faqs.length;
  };

  const isCategoryPartiallySelected = (categoryId: string) => {
    const category = availableCategories.find((c) => c.id === categoryId);
    if (!category) return false;
    const selected = selectedQuestions[categoryId] || [];
    return selected.length > 0 && selected.length < category.faqs.length;
  };

  const getTotalSelectedCount = () => {
    return Object.values(selectedQuestions).reduce((sum, questions) => sum + questions.length, 0);
  };

  const handleClose = () => {
    setExpandedCategories([]);
    setSelectedQuestions({});
    onClose();
  };

  const handleImport = () => {
    // Build categories with only selected questions
    const categoriesToImport: FAQCategory[] = [];

    Object.entries(selectedQuestions).forEach(([categoryId, questionIds]) => {
      if (questionIds.length > 0) {
        const masterCategory = masterCategoriesWithQuestions.find((c) => c.id === categoryId);
        if (masterCategory) {
          const selectedFaqs = masterCategory.faqs.filter((f) => questionIds.includes(f.id));
          categoriesToImport.push({
            ...masterCategory,
            faqs: selectedFaqs,
            count: selectedFaqs.length,
          });
        }
      }
    });

    onImport(categoriesToImport);
    handleClose();
  };

  const totalSelected = getTotalSelectedCount();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          fontFamily: 'Manrope, sans-serif',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600} sx={{ fontFamily: 'Manrope, sans-serif' }}>
            Import from Master Tenant
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, fontFamily: 'Manrope, sans-serif' }}
          >
            Select categories and questions to import. Expand categories to select individual
            questions.
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ mt: -0.5, mr: -1 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {availableCategories.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              All categories and questions have already been imported.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {availableCategories.map((category) => (
              <Box
                key={category.id}
                sx={{
                  border: '1px solid #EFF0F0',
                  borderRadius: 2,
                  mb: 1,
                  overflow: 'hidden',
                }}
              >
                {/* Category Header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.5,
                    px: 2,
                    bgcolor: expandedCategories.includes(category.id) ? '#F5F5F5' : '#FFFFFF',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#F5F5F5' },
                  }}
                  onClick={() => handleToggleExpand(category.id)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      checked={isCategoryFullySelected(category.id)}
                      indeterminate={isCategoryPartiallySelected(category.id)}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleCategory(category.id);
                      }}
                    />
                    <Box sx={{ ml: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {category.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {category.faqs.length} questions available
                        {(selectedQuestions[category.id]?.length || 0) > 0 &&
                          ` • ${selectedQuestions[category.id]?.length} selected`}
                      </Typography>
                    </Box>
                  </Box>
                  <ChevronUpIcon
                    sx={{
                      width: 20,
                      height: 20,
                      transform: expandedCategories.includes(category.id)
                        ? 'rotate(0deg)'
                        : 'rotate(180deg)',
                      transition: 'transform 0.2s',
                    }}
                  />
                </Box>

                {/* Questions List */}
                <Collapse in={expandedCategories.includes(category.id)}>
                  <Box sx={{ bgcolor: '#FAFAFA' }}>
                    {category.faqs.map((faq) => (
                      <Box
                        key={faq.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          py: 1,
                          px: 2,
                          pl: 5,
                          borderTop: '1px solid #EFF0F0',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: '#F0F0F0' },
                        }}
                        onClick={() => handleToggleQuestion(category.id, faq.id)}
                      >
                        <Checkbox
                          checked={selectedQuestions[category.id]?.includes(faq.id) || false}
                          size="small"
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => handleToggleQuestion(category.id, faq.id)}
                        />
                        <Box sx={{ ml: 1, flex: 1 }}>
                          <Typography variant="body2">{faq.question}</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                            {faq.cohorts.map((cohort, idx) => (
                              <Chip
                                key={idx}
                                label={cohort}
                                size="small"
                                sx={{
                                  bgcolor: theme.palette.accentsYellowA.light,
                                  fontSize: 11,
                                  height: 20,
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderColor: '#E0E0E0',
            color: theme.palette.textDarkPrimary.primary,
            fontFamily: 'Manrope, sans-serif',
            px: 3,
            '&:hover': {
              borderColor: '#BDBDBD',
              bgcolor: '#FAFAFA',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disabled={totalSelected === 0}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontFamily: 'Manrope, sans-serif',
            px: 3,
            bgcolor: totalSelected > 0 ? theme.palette.accentsYellowA.main : '#E0E0E0',
            color: totalSelected > 0 ? theme.palette.textDarkPrimary.primary : '#9E9E9E',
            '&:hover': {
              bgcolor: totalSelected > 0 ? theme.palette.accentsOrangeB.main : '#E0E0E0',
            },
            '&.Mui-disabled': {
              bgcolor: '#E0E0E0',
              color: '#9E9E9E',
            },
          }}
        >
          Import{' '}
          {totalSelected > 0 ? `${totalSelected} question${totalSelected > 1 ? 's' : ''}` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PrototypeFAQs: React.FC = () => {
  const { isMasterTenant, selectedTenant } = usePrototype();
  // Master tenant sees all categories, other tenants start with empty list
  const [categories, setCategories] = useState<FAQCategory[]>(
    isMasterTenant ? initialCategories : []
  );
  const [activeTab, setActiveTab] = useState(0);
  const [expandedCategories, setExpandedCategories] = useState<string[]>(
    isMasterTenant ? ['2'] : []
  );
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<FAQCategory | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Reset categories when tenant changes
  useEffect(() => {
    if (isMasterTenant) {
      setCategories(initialCategories);
      setExpandedCategories(['2']);
    } else {
      setCategories([]);
      setExpandedCategories([]);
    }
  }, [selectedTenant, isMasterTenant]);

  const handleToggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]
    );
  };

  const handleCreateCategory = (name: string) => {
    const newCategory: FAQCategory = {
      id: String(categories.length + 1),
      name,
      count: 0,
      faqs: [],
    };
    setCategories([...categories, newCategory]);
    setSnackbarMessage(`"${name}" category has been added`);
    setSnackbarOpen(true);
  };

  const handleImportCategories = (importedCategories: FAQCategory[]) => {
    let totalQuestions = 0;
    const updatedCategories = [...categories];

    importedCategories.forEach((importCat) => {
      totalQuestions += importCat.faqs.length;

      // Check if category already exists
      const existingCatIndex = updatedCategories.findIndex(
        (c) => c.name.toLowerCase() === importCat.name.toLowerCase()
      );

      if (existingCatIndex >= 0) {
        // Merge questions into existing category
        const existingCat = updatedCategories[existingCatIndex];
        const newFaqs = importCat.faqs.filter(
          (faq) => !existingCat.faqs.some((f) => f.id === faq.id)
        );
        updatedCategories[existingCatIndex] = {
          ...existingCat,
          faqs: [...existingCat.faqs, ...newFaqs],
          count: existingCat.count + newFaqs.length,
        };
      } else {
        // Add new category
        updatedCategories.push({
          ...importCat,
          id: String(Date.now() + Math.random()),
        });
      }
    });

    setCategories(updatedCategories);
    setSnackbarMessage(
      `${totalQuestions} question${totalQuestions > 1 ? 's' : ''} imported successfully`
    );
    setSnackbarOpen(true);
  };

  const handleRenameCategory = (newName: string) => {
    if (selectedCategory) {
      setCategories((prev) =>
        prev.map((cat) => (cat.id === selectedCategory.id ? { ...cat, name: newName } : cat))
      );
      setSnackbarMessage(`Category renamed to "${newName}"`);
      setSnackbarOpen(true);
    }
  };

  const handleDeleteCategory = () => {
    if (selectedCategory) {
      setCategories((prev) => prev.filter((cat) => cat.id !== selectedCategory.id));
      setSnackbarMessage(`"${selectedCategory.name}" category has been deleted`);
      setSnackbarOpen(true);
    }
  };

  const handleMoveAndDelete = () => {
    // Close delete dialog and open move dialog
    setDeleteDialogOpen(false);
    setMoveDialogOpen(true);
  };

  const handleMoveQuestionsAndDelete = (destinationId: string) => {
    if (selectedCategory) {
      const destinationCategory = categories.find((c) => c.id === destinationId);
      // Move questions count to destination and delete source
      setCategories((prev) =>
        prev
          .map((cat) =>
            cat.id === destinationId
              ? { ...cat, count: cat.count + (selectedCategory.count || 0) }
              : cat
          )
          .filter((cat) => cat.id !== selectedCategory.id)
      );
      setSnackbarMessage(
        `${selectedCategory.count} questions moved to "${destinationCategory?.name}" and "${selectedCategory.name}" deleted`
      );
      setSnackbarOpen(true);
      setSelectedCategory(null);
    }
  };

  const openRenameDialog = (category: FAQCategory) => {
    setSelectedCategory(category);
    setRenameDialogOpen(true);
  };

  const openDeleteDialog = (category: FAQCategory) => {
    setSelectedCategory(category);
    setDeleteDialogOpen(true);
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#F7F5F2', minHeight: '100%', fontFamily: 'Manrope, sans-serif' }}>
      <Breadcrumb items={['Sunny Benefits', 'Content', 'FAQs']} />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="title1" component="h1">
          FAQs
        </Typography>
        {isMasterTenant ? (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              bgcolor: theme.palette.accentsYellowA.main,
              color: theme.palette.textDarkPrimary.primary,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                bgcolor: theme.palette.accentsOrangeB.main,
              },
            }}
          >
            New Category
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => setImportDialogOpen(true)}
            sx={{
              bgcolor: theme.palette.accentsYellowA.main,
              color: theme.palette.textDarkPrimary.primary,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                bgcolor: theme.palette.accentsOrangeB.main,
              },
            }}
          >
            Import Category
          </Button>
        )}
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{
          mb: 2,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            minWidth: 'auto',
            px: 2,
          },
          '& .Mui-selected': {
            bgcolor: '#FFFFFF',
            borderRadius: '8px 8px 0 0',
          },
        }}
      >
        <Tab label="Categories" />
        <Tab label="History" />
      </Tabs>

      {/* Search and Filter */}
      <Box
        sx={{
          bgcolor: '#FFFFFF',
          borderRadius: 2,
          p: 2,
          mb: 2,
          border: '1px solid #EFF0F0',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <TextField
            placeholder="Search"
            size="small"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
          <Button
            startIcon={<FilterListIcon />}
            sx={{ color: 'text.secondary', textTransform: 'none' }}
          >
            Filter
          </Button>
        </Box>
      </Box>

      {/* Categories */}
      <Box sx={{ mb: 3 }}>
        {categories.length === 0 ? (
          <Box
            sx={{
              bgcolor: '#FFFFFF',
              border: '1px solid #EFF0F0',
              borderRadius: 2,
              p: 6,
              textAlign: 'center',
            }}
          >
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              No FAQ categories yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isMasterTenant
                ? 'Click "New Category" to create your first category.'
                : 'Click "Import Category" to import categories from Master tenant.'}
            </Typography>
          </Box>
        ) : (
          categories.map((category) => (
            <CategoryAccordion
              key={category.id}
              category={category}
              isExpanded={expandedCategories.includes(category.id)}
              onToggle={() => handleToggleCategory(category.id)}
              onRename={() => openRenameDialog(category)}
              onDelete={() => openDeleteDialog(category)}
              showRename={true}
              showDelete={isMasterTenant}
            />
          ))
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#FFFFFF',
          borderRadius: 2,
          p: 2,
          border: '1px solid #EFF0F0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Show [table data]
          </Typography>
          <Select
            size="small"
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
            sx={{ minWidth: 100 }}
          >
            <MenuItem value={10}>10 per page</MenuItem>
            <MenuItem value={25}>25 per page</MenuItem>
            <MenuItem value={50}>50 per page</MenuItem>
          </Select>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            1-25 of 100
          </Typography>
          <Pagination
            count={4}
            page={page}
            onChange={(_, value) => setPage(value)}
            size="small"
            siblingCount={0}
          />
        </Box>
      </Box>

      {/* Create Category Dialog */}
      <CreateCategoryDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSave={handleCreateCategory}
        existingCategories={categories.map((c) => c.name)}
      />

      {/* Rename Category Dialog */}
      <RenameCategoryDialog
        open={renameDialogOpen}
        onClose={() => {
          setRenameDialogOpen(false);
          setSelectedCategory(null);
        }}
        onSave={handleRenameCategory}
        category={selectedCategory}
        existingCategories={categories.map((c) => c.name)}
      />

      {/* Delete Category Dialog */}
      <DeleteCategoryDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedCategory(null);
        }}
        onConfirm={handleDeleteCategory}
        onMoveAndDelete={handleMoveAndDelete}
        category={selectedCategory}
      />

      {/* Move Questions Dialog */}
      <MoveQuestionsDialog
        open={moveDialogOpen}
        onClose={() => {
          setMoveDialogOpen(false);
          setSelectedCategory(null);
        }}
        onConfirm={handleMoveQuestionsAndDelete}
        category={selectedCategory}
        categories={categories}
      />

      {/* Import Category Dialog */}
      <ImportCategoryDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImport={handleImportCategories}
        existingCategories={categories}
      />

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          icon={<CheckCircleOutlineIcon sx={{ color: '#2E7D32' }} />}
          sx={{
            bgcolor: '#E8F5E9',
            color: '#1B5E20',
            fontFamily: 'Manrope, sans-serif',
            '& .MuiAlert-message': {
              fontWeight: 500,
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PrototypeFAQs;
